import { useEffect, useState } from "react"
import { currencyFormatter } from "../../../utils/formatter"
import { Project } from "../../../types/project.type"

import Placeholder from "../../../assets/images/placeholder.png"
import { Product } from "../../../types/product.type"
import { ProductCategory } from "../../../types/product.type"
import { useGetProducts } from "../../../hooks/useGetProducts"
import { resolveOptionalColor, resolveThemeColor } from "../../../utils/buttonColor"

function ProductSection({ project }: { project: Project }) {
  const { products, productCategories } = useGetProducts()
  const [selectedProductCategory, setSelectedProductCategory] = useState<ProductCategory>()
  const [displayedProducts, setDisplayedProducts] = useState<Product[]>([])

  const themeColor = resolveThemeColor(project.themeColor)
  const serviceColor = resolveOptionalColor(project.serviceColor)

  useEffect(() => {
    setSelectedProductCategory(productCategories[0])
  }, [productCategories])

  useEffect(() => {
    setDisplayedProducts(
      products.filter(
        (product: Product) => product.categoryId === selectedProductCategory?.productCategoryId
      )
    )
  }, [products, selectedProductCategory])

  if (products.length === 0) return null

  return (
    <section
      className="mx-5 mt-8 mb-8 overflow-hidden rounded-2xl border border-gray-200/90 bg-white text-gray-900 shadow-xl shadow-gray-900/5 sm:mt-10"
      id="products"
    >
      <header
        className="border-b border-gray-100 px-5 py-6 sm:px-8 sm:py-8"
        style={{
          background: `linear-gradient(135deg, ${themeColor}0d 0%, transparent 55%), linear-gradient(to bottom, #fafafa, #ffffff)`,
        }}
      >
        <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Products</h2>
        <p className="mt-1.5 max-w-lg text-sm leading-relaxed text-gray-600">
          Pilih kategori untuk melihat katalog produk.
        </p>
      </header>

      <div className="px-5 pb-10 pt-6 sm:px-8">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Kategori</p>
        <div
          className="-mx-1 mt-3 flex flex-row items-start gap-2 overflow-x-auto pb-2 [-webkit-overflow-scrolling:touch] sm:gap-3"
          role="tablist"
          aria-label="Kategori produk"
        >
          {productCategories.map((category: ProductCategory) => {
            const isSelected =
              selectedProductCategory?.productCategoryId === category.productCategoryId
            return (
              <button
                type="button"
                key={category.productCategoryId}
                role="tab"
                aria-selected={isSelected}
                className={`flex shrink-0 flex-col items-center gap-2 rounded-xl p-2 text-center transition-colors ${
                  isSelected ? "border-2 bg-gray-50/90" : "border-2 border-transparent hover:bg-gray-50/80"
                }`}
                style={isSelected ? { borderColor: themeColor } : undefined}
                onClick={() => setSelectedProductCategory(category)}
              >
                <span className="block overflow-hidden rounded-xl bg-gray-100 shadow-sm ring-1 ring-gray-200/80">
                  <img
                    src={category.files?.[0]?.s3Url || Placeholder}
                    alt={category.categoryName}
                    className="h-20 w-20 object-cover sm:h-24 sm:w-24"
                  />
                </span>
                <span
                  className="max-w-[6.5rem] break-words text-center text-xs font-semibold leading-tight sm:text-sm"
                  style={{ color: isSelected ? themeColor : "#374151" }}
                >
                  {category.categoryName}
                </span>
              </button>
            )
          })}
        </div>

        <div
          key={selectedProductCategory?.productCategoryId ?? 'empty'}
          className="mt-8 space-y-4"
        >
          {displayedProducts.length === 0 ? (
            <p className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 py-10 text-center text-sm text-gray-500">
              Belum ada produk dalam kategori ini.
            </p>
          ) : (
            displayedProducts.map((product) => (
              <article
                key={product.productId}
                className="flex flex-col gap-4 rounded-xl border border-gray-200/90 p-4 text-left shadow-sm transition-shadow hover:shadow-md sm:flex-row sm:items-stretch sm:justify-between sm:gap-6 sm:p-5"
                style={{
                  borderLeftWidth: 4,
                  borderLeftColor: themeColor,
                  ...(serviceColor
                    ? {
                        background: `linear-gradient(135deg, ${serviceColor}12 0%, #ffffff 42%)`,
                      }
                    : { backgroundColor: "#ffffff" }),
                  borderTopColor: "#e5e7eb",
                  borderRightColor: "#e5e7eb",
                  borderBottomColor: "#e5e7eb",
                }}
              >
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-semibold leading-snug" style={{ color: themeColor }}>
                    {product.name}
                  </h3>
                  {product.subtitle ? (
                    <p className="mt-1 text-sm font-medium text-gray-500">{product.subtitle}</p>
                  ) : null}
                  {product.description ? (
                    <div
                      className="mt-2 w-full min-w-0 max-w-none text-left text-sm leading-relaxed text-gray-600 [overflow-wrap:anywhere] [&_*]:!max-w-full [&_*]:!text-left [&_a]:font-medium [&_a]:underline [&_a]:decoration-gray-400 [&_div]:!w-full [&_img]:max-w-full [&_img]:h-auto [&_p]:!w-full"
                      dangerouslySetInnerHTML={{ __html: product.description }}
                    />
                  ) : null}
                  {product.specification?.length ? (
                    <dl className="mt-3 space-y-1.5 border-t border-gray-100 pt-3">
                      {product.specification.map(
                        (spec, idx) =>
                          spec.key &&
                          spec.value && (
                            <div
                              key={`${spec.key}-${idx}`}
                              className="flex flex-wrap gap-x-2 text-xs text-gray-600"
                            >
                              <dt className="font-medium text-gray-500">{spec.key}</dt>
                              <dd>{spec.value}</dd>
                            </div>
                          )
                      )}
                    </dl>
                  ) : null}
                </div>

                <div className="flex shrink-0 flex-col gap-3 border-t border-gray-100 pt-4 sm:w-52 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-5">
                  <div className="flex flex-row items-center justify-between gap-3 sm:flex-col sm:items-end sm:justify-start sm:text-right">
                    <p className="text-lg font-semibold tabular-nums" style={{ color: themeColor }}>
                      {currencyFormatter(product.price)}
                    </p>
                    <img
                      src={product.files?.[0]?.s3Url || Placeholder}
                      alt={product.name}
                      className="h-16 w-16 shrink-0 rounded-lg border border-gray-200 object-cover sm:h-20 sm:w-20"
                    />
                  </div>
                  {product.stock != null ? (
                    <p className="text-right text-xs text-gray-500 sm:text-right">
                      Stok:{" "}
                      <span className="font-medium text-gray-700">{product.stock}</span>
                    </p>
                  ) : null}
                </div>
              </article>
            ))
          )}
        </div>
      </div>
    </section>
  )
}

export default ProductSection
